import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Remembering the last section is a convenience; the address still says where you are
  }
};

/**
 * The open section of a module page, kept in the address (/purchase/vendors) so the back button moves between
 * sections and a section can be bookmarked or shared. The route is `${base}/:section?`.
 *
 * Opening the module without a section (/purchase), or with one it doesn't have, goes to the section last
 * opened there, else defaultKey (else the first key). Choosing a section adds a history entry.
 *
 * @param {string} base the module's path, such as '/purchase'
 * @param {string[]} keys the module's section keys
 * @param {string} [defaultKey]
 * @returns {[string, (key: string) => void]} the open section and a function to open another
 */
export default function useSectionTab(base, keys, defaultKey = keys[0]) {
  const { section } = useParams();
  const navigate = useNavigate();
  const storageKey = `activeTab_${base}`;

  const valid = Boolean(section) && keys.includes(section);
  const remembered = read(storageKey);
  const fallback = remembered && keys.includes(remembered) ? remembered : defaultKey;
  const active = valid ? section : fallback;

  useEffect(() => {
    if (!valid) navigate(`${base}/${fallback}`, { replace: true });
  }, [valid, fallback, base, navigate]);

  useEffect(() => {
    if (valid) write(storageKey, section);
  }, [valid, section, storageKey]);

  const open = useCallback((key) => {
    if (key && key !== active) navigate(`${base}/${key}`);
  }, [active, base, navigate]);

  return [active, open];
}
